import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Popover,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { AddGradeModal } from './AddGradeModal';
import { EditGradeModal } from './EditGradeModal';
import { ImportGradeModal } from './ImportGradeModal';
import { ImportGradeV2Modal } from './ImportGradeV2Modal';
import GradeImporterAPI, {
  type UploadGradeBlocksResult,
  type FailedGrade,
  type BulkSaveGradesResult,
} from 'api/grade-importer.api';
import { extractApiErrorMessage } from 'utils/helpers/apiError';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import { type GradeRecord } from './GradeImporterView';
import {
  type MaterialCategoryRecord,
  type GradeCategoryRecord,
} from './CategoryManagement/types';

type GradePayload = Omit<GradeRecord, 'id'>;

// Inventory is flagged as stale once it hasn't been updated within this many hours.
const INVENTORY_STALE_HOURS = 24;
const PAGE_SIZE = 25;
const NUMERIC_GRADE_FIELDS = new Set(['inventory', 'specificGravity']);

const fmtDouble = ({ value }: { value: unknown }) => {
  const n = Number(value);
  return !isNaN(n) ? n.toFixed(2) : '—';
};

const staleDurationParts = (
  hours: number
): { unit: 'hour' | 'day'; count: number } => {
  if (hours < 24) {
    return { unit: 'hour', count: Math.max(1, Math.floor(hours)) };
  }
  return { unit: 'day', count: Math.floor(hours / 24) };
};

interface Props {
  materialCategories: MaterialCategoryRecord[];
  gradeCategories: GradeCategoryRecord[];
  onRefetchMaterialCategories: () => Promise<void>;
  onRefetchGradeCategories: () => Promise<void>;
}

export const GradesTab = ({
  materialCategories,
  gradeCategories,
  onRefetchMaterialCategories,
  onRefetchGradeCategories,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { accessToken, customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;
  const gradeApi = useRef(new GradeImporterAPI()).current;

  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importV2Open, setImportV2Open] = useState(false);
  const [editGrade, setEditGrade] = useState<GradeRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [importResult, setImportResult] = useState<{
    created: number;
    failedGrades: FailedGrade[];
  } | null>(null);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>({
    name: true,
    materialCategory: true,
    locationName: true,
    blend: true,
    dump: false,
    gradeCategory: false,
    specification: false,
    active: true,
    inventory: true,
    specificGravity: true,
    qualities: true,
  });

  const fetchGrades = useCallback(async () => {
    if (!accessToken || !customerId) return;
    setLoading(true);
    try {
      const data = await gradeApi.getGrades();
      setGrades(
        data.map((r) => ({
          ...r,
          qualities: Object.fromEntries(
            Object.entries(r.qualities ?? {}).map(([k, v]) => [k, Number(v)])
          ),
        }))
      );
    } catch (e) {
      console.error(e);
      dispatch(
        showNotification({
          message: t('grade_importer_fetch_failed'),
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, customerId, dispatch, gradeApi, t]);

  useEffect(() => {
    void fetchGrades();
  }, [fetchGrades]);

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  const downloadFailedGrades = (failedGrades: FailedGrade[]) => {
    const rows = failedGrades.map((g) => ({
      NAME: g.name,
      MATERIAL: String(g.materialCategory ?? ''),
      LOCATION: String(g.locationName ?? ''),
      BLEND: String(g.blend ?? ''),
      DUMP: String(g.dump ?? ''),
      CATEGORY: String(g.gradeCategory ?? ''),
      SPECIFICATION: String(g.specification ?? ''),
      INVENTORY: String(g.inventory ?? ''),
      SPECIFIC_GRAVITY: String(g.specificGravity ?? ''),
      QUALITIES: g.qualities
        ? Object.entries(g.qualities as Record<string, number>)
            .map(([k, v]) => `${k.toUpperCase()}:${v}`)
            .join(', ')
        : '',
      REASON: g.reason,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Grades');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `failed_grades_${Date.now()}.xlsx`
    );
  };

  const bulkSave = async (
    gradeList: GradePayload[]
  ): Promise<BulkSaveGradesResult> => {
    try {
      return await gradeApi.bulkCreate(gradeList);
    } catch (e) {
      throw new Error(extractApiErrorMessage(e, 'Save failed'));
    }
  };

  const handleAdd = async (grade: GradeRecord) => {
    try {
      const { id: _id, ...payload } = grade;
      const result = await bulkSave([payload]);
      await fetchGrades();
      if (result.failedGrades.length > 0) {
        const reason = result.failedGrades[0].reason;
        toast(reason, 'error');
        throw new Error(reason);
      }
      setAddOpen(false);
      toast(t('grade_importer_toast_added'), 'success');
    } catch (e) {
      if (!(e instanceof Error)) {
        toast(t('grade_importer_toast_add_failed'), 'error');
      }
      throw e;
    }
  };

  const handleImport = async (rows: GradeRecord[]) => {
    try {
      const payloads = rows.map(({ id: _id, ...rest }) => rest);
      const result = await bulkSave(payloads);
      await fetchGrades();
      setImportOpen(false);
      if (result.failedGrades.length > 0) {
        setImportResult({
          created: result.created,
          failedGrades: result.failedGrades,
        });
        toast(
          t('grade_importer_toast_partial', {
            created: result.created,
            failed: result.failed,
          }),
          result.created > 0 ? 'success' : 'error'
        );
      } else {
        toast(
          t('grade_importer_toast_imported', { count: result.created }),
          'success'
        );
      }
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('grade_importer_toast_import_failed'),
        'error'
      );
    }
  };

  const handleUploadV2 = async (
    file: File
  ): Promise<UploadGradeBlocksResult> => {
    const result = await gradeApi.uploadGradeBlocks(file);
    await fetchGrades();
    toast(result.message, result.gradesFailed > 0 ? 'error' : 'success');
    return result;
  };

  const handleUpdate = async (updated: Omit<GradeRecord, 'id'>) => {
    if (!editGrade) return;
    try {
      await gradeApi.updateGrade(editGrade.id, updated);
      await fetchGrades();
      setEditGrade(null);
      toast(t('grade_importer_toast_updated'), 'success');
    } catch (e) {
      toast(
        extractApiErrorMessage(e, t('grade_importer_toast_update_failed')),
        'error'
      );
      throw e;
    }
  };

  const handleDelete = useCallback((id: string, name: string) => {
    setDeleteTarget({ id, name });
    setDeleteConfirmText('');
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await gradeApi.deleteGrade(deleteTarget.id);
      await fetchGrades();
      toast(
        t('grade_importer_toast_deleted', { name: deleteTarget.name }),
        'success'
      );
    } catch (e) {
      toast(
        extractApiErrorMessage(e, t('grade_importer_toast_delete_failed')),
        'error'
      );
    } finally {
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  };

  const gridWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return;

    let scroller: HTMLElement | null = null;
    let actionsHeader: HTMLElement | null = null;

    const pin = () => {
      if (!scroller || !actionsHeader) return;
      if (scroller.scrollWidth <= scroller.clientWidth) {
        actionsHeader.style.transform = '';
        return;
      }
      const cw = scroller.clientWidth;
      const hw = actionsHeader.offsetWidth;
      const hl = actionsHeader.offsetLeft;
      const sl = scroller.scrollLeft;
      actionsHeader.style.transform = `translateX(${cw - hw - hl + sl}px)`;
      actionsHeader.style.backgroundColor = 'var(--header-bg-dark)';
      actionsHeader.style.zIndex = '5';
    };

    const setup = () => {
      scroller = wrap.querySelector<HTMLElement>(
        '.MuiDataGrid-virtualScroller'
      );
      actionsHeader = wrap.querySelector<HTMLElement>(
        '.MuiDataGrid-columnHeader[data-field="actions"]'
      );
      if (scroller && actionsHeader) {
        scroller.addEventListener('scroll', pin);
        pin();
      }
    };

    const timer = setTimeout(setup, 50);

    return () => {
      clearTimeout(timer);
      scroller?.removeEventListener('scroll', pin);
    };
  }, [grades.length]);

  const hasData = grades.length > 0;

  const filteredGrades = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return grades;
    return grades.filter((g) => {
      const qualitiesStr = Object.keys(g.qualities ?? {})
        .join(' ')
        .toLowerCase();
      return (
        String(g.name ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.materialCategory ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.locationName ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.blend ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.dump ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.gradeCategory ?? '')
          .toLowerCase()
          .includes(q) ||
        String(g.specification ?? '')
          .toLowerCase()
          .includes(q) ||
        qualitiesStr.includes(q)
      );
    });
  }, [grades, searchQuery]);

  // reset to page 0 whenever search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const sortedGrades = useMemo(() => {
    if (sortModel.length === 0) return filteredGrades;
    const { field, sort } = sortModel[0];
    const dir = sort === 'desc' ? -1 : 1;
    return [...filteredGrades].sort((a, b) => {
      if (NUMERIC_GRADE_FIELDS.has(field)) {
        const av = Number(a[field as keyof GradeRecord] ?? 0);
        const bv = Number(b[field as keyof GradeRecord] ?? 0);
        return (av - bv) * dir;
      }
      const av = String(a[field as keyof GradeRecord] ?? '').toLowerCase();
      const bv = String(b[field as keyof GradeRecord] ?? '').toLowerCase();
      return av.localeCompare(bv) * dir;
    });
  }, [filteredGrades, sortModel]);

  const pageCount = Math.max(1, Math.ceil(sortedGrades.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedGrades = sortedGrades.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );
  const hasResults = filteredGrades.length > 0;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('grade_importer_col_name'),
        minWidth: 160,
      },
      {
        field: 'materialCategory',
        headerName: t('grade_importer_col_material'),
        minWidth: 130,
      },
      {
        field: 'locationName',
        headerName: t('grade_importer_col_location_name'),
        minWidth: 160,
      },
      {
        field: 'blend',
        headerName: t('grade_importer_col_blend'),
        minWidth: 120,
      },
      {
        field: 'dump',
        headerName: t('grade_importer_col_dump'),
        minWidth: 120,
      },
      {
        field: 'gradeCategory',
        headerName: t('grade_importer_col_category'),
        minWidth: 120,
      },
      {
        field: 'specification',
        headerName: t('grade_importer_col_specification'),
        minWidth: 130,
      },
      {
        field: 'active',
        headerName: t('grade_importer_col_active'),
        minWidth: 90,
        renderCell: (params) => (
          <Chip
            label={
              params.value
                ? t('grade_importer_chip_yes')
                : t('grade_importer_chip_no')
            }
            size="small"
            color={params.value ? 'success' : 'default'}
            sx={{ fontSize: '0.7rem' }}
          />
        ),
      },
      {
        field: 'inventory',
        headerName: t('grade_importer_col_inventory'),
        minWidth: 140,
        type: 'number',
        renderCell: ({
          row,
          value,
        }: {
          row: GradeRecord;
          value?: number | null;
        }) => {
          const isMissing = Number(value) === 0;
          const updatedAtMs = row.updatedAt ? Date.parse(row.updatedAt) : NaN;
          const hoursSinceUpdate = !isNaN(updatedAtMs)
            ? (Date.now() - updatedAtMs) / (1000 * 60 * 60)
            : null;
          const isStale =
            hoursSinceUpdate !== null &&
            hoursSinceUpdate >= INVENTORY_STALE_HOURS;

          const detail = isMissing
            ? t('grade_importer_inventory_missing')
            : isStale
            ? t('grade_importer_inventory_stale', {
                duration: (() => {
                  const { unit, count } = staleDurationParts(hoursSinceUpdate);
                  return t(
                    `grade_importer_duration_${unit}${count === 1 ? '' : 's'}`,
                    { count }
                  );
                })(),
              })
            : null;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">{fmtDouble({ value })}</Typography>
              {detail && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" fontWeight={700}>
                        {t('grade_importer_action_required')}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {detail}
                      </Typography>
                    </Box>
                  }
                >
                  <WarningAmberIcon
                    sx={{ fontSize: 16, color: 'warning.main' }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'specificGravity',
        headerName: t('grade_importer_col_specific_gravity'),
        minWidth: 160,
        type: 'number',
        valueFormatter: fmtDouble,
      },
      {
        field: 'qualities',
        headerName: t('grade_importer_col_qualities'),
        minWidth: 300,
        renderCell: ({ value }: { value?: Record<string, number> }) =>
          value && Object.keys(value).length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                py: 0.75,
                alignContent: 'flex-start',
              }}
            >
              {Object.entries(value).map(([k, v]) => (
                <Chip
                  key={k}
                  label={`${k.toUpperCase()}: ${v}`}
                  size="small"
                  sx={{
                    fontSize: '0.68rem',
                    height: 20,
                    fontFamily: 'monospace',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          ) : (
            '—'
          ),
      },
      {
        field: 'actions',
        headerName: t('grade_importer_col_actions'),
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Tooltip title={t('grade_importer_edit')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditGrade(params.row as GradeRecord);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('grade_importer_delete')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(
                    params.row.id as string,
                    params.row.name as string
                  );
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleDelete, t]
  );

  const activeCount = grades.filter((g) => g.active).length;

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.4,
              py: 0.35,
              borderRadius: '6px',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.22),
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'primary.main',
              }}
            />
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {grades.length} {t('grade_importer_total')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.4,
              py: 0.35,
              borderRadius: '6px',
              bgcolor: (th) =>
                alpha(
                  activeCount > 0
                    ? th.palette.success.main
                    : th.palette.primary.main,
                  0.1
                ),
              border: '1px solid',
              borderColor: (th) =>
                alpha(
                  activeCount > 0
                    ? th.palette.success.main
                    : th.palette.primary.main,
                  0.22
                ),
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: activeCount > 0 ? 'success.main' : 'text.disabled',
              }}
            />
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {activeCount} {t('grade_importer_active')}
            </Typography>
          </Box>
        </Box>
        <Tooltip title={t('grade_importer_btn_refresh')}>
          <span>
            <IconButton
              onClick={() => {
                void fetchGrades();
                void onRefetchMaterialCategories();
                void onRefetchGradeCategories();
              }}
              disabled={loading}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.6),
                borderRadius: '8px',
                p: 0.8,
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.5s',
                  transform: loading ? 'rotate(360deg)' : 'none',
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddOpen(true);
          }}
          sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
        >
          {t('grade_importer_btn_add')}
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            setImportOpen(true);
          }}
          sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
        >
          {t('grade_importer_btn_import')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            setImportV2Open(true);
          }}
          sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
        >
          {t('grade_importer_btn_import_v2')}
        </Button>
      </Box>

      {loading && grades.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : !hasData ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.divider, 0.5),
          }}
        >
          <LayersOutlinedIcon
            sx={{ fontSize: 40, color: 'primary.main', mb: 2 }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            textAlign="center"
          >
            {t('grade_importer_empty_title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 1, mb: 3, maxWidth: 360 }}
          >
            {t('grade_importer_empty_body')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setAddOpen(true);
              }}
            >
              {t('grade_importer_btn_add')}
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => {
                setImportOpen(true);
              }}
            >
              {t('grade_importer_btn_import')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t('grade_importer_all_grades')}{' '}
              <Typography
                component="span"
                variant="body2"
                color="primary.main"
                fontWeight={700}
              >
                (
                {searchQuery.trim()
                  ? `${filteredGrades.length} / ${grades.length}`
                  : grades.length}
                )
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                size="small"
                placeholder={t('grade_importer_search_placeholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                        }}
                        edge="end"
                        sx={{ p: 0.25 }}
                      >
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: 220,
                  mr: 0.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                  },
                }}
              />
              <Button
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                onClick={(e) => {
                  setColAnchor(e.currentTarget);
                }}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.76rem',
                  borderRadius: '6px',
                }}
              >
                {t('grade_importer_filters')}
              </Button>
              <Popover
                open={Boolean(colAnchor)}
                anchorEl={colAnchor}
                onClose={() => {
                  setColAnchor(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
                    p: 1.5,
                    minWidth: 200,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    px: 0.5,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  {t('grade_importer_col_visibility')}
                </Typography>
                {Object.keys(colVisibility).map((col) => (
                  <Box key={col} sx={{ display: 'block' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={colVisibility[col]}
                          onChange={(e) => {
                            setColVisibility((prev) => ({
                              ...prev,
                              [col]: e.target.checked,
                            }));
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.82rem' }}
                        >
                          {t(
                            `grade_importer_col_${
                              col === 'locationName'
                                ? 'location_name'
                                : col === 'specificGravity'
                                ? 'specific_gravity'
                                : col === 'materialCategory'
                                ? 'material'
                                : col === 'gradeCategory'
                                ? 'category'
                                : col
                            }`
                          )}
                        </Typography>
                      }
                      sx={{ mx: 0, width: '100%' }}
                    />
                  </Box>
                ))}
              </Popover>
            </Box>
          </Box>
          {hasResults ? (
            <Box
              ref={gridWrapRef}
              sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (th) => alpha(th.palette.background.paper, 0.6),
                    zIndex: 10,
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}
              <CustomStyledDataGrid
                rows={pagedGrades}
                columns={columns}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                hideFooter
                columnVisibilityModel={colVisibility}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  setSortModel(model);
                }}
                sx={{
                  '& .MuiDataGrid-cell[data-field="actions"]': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    borderLeft: '1px solid var(--border-color-dark)',
                    backgroundColor: 'var(--row-bg-dark)',
                  },
                  '& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field="actions"]':
                    { backgroundColor: 'var(--even-row-bg-dark)' },
                  '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="actions"]':
                    { backgroundColor: 'var(--hover-bg-dark)' },
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {t('grade_importer_no_results')}
              </Typography>
            </Box>
          )}
          {hasResults && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.divider, 0.4),
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setPage((p) => Math.max(0, p - 1));
                }}
                disabled={safePage === 0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  p: 0.5,
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 80, textAlign: 'center' }}
              >
                {safePage + 1} / {pageCount}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setPage((p) => Math.min(pageCount - 1, p + 1));
                }}
                disabled={safePage >= pageCount - 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  p: 0.5,
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <AddGradeModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={async (grade) => {
          await handleAdd(grade);
        }}
        materialCategories={materialCategories}
        gradeCategories={gradeCategories}
        onRefetchMaterialCategories={onRefetchMaterialCategories}
        onRefetchGradeCategories={onRefetchGradeCategories}
      />
      <EditGradeModal
        open={editGrade !== null}
        grade={editGrade}
        onClose={() => {
          setEditGrade(null);
        }}
        onSave={async (updated) => {
          await handleUpdate(updated);
        }}
        materialCategories={materialCategories}
        gradeCategories={gradeCategories}
        onRefetchMaterialCategories={onRefetchMaterialCategories}
        onRefetchGradeCategories={onRefetchGradeCategories}
      />
      <ImportGradeModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImport={async (rows) => {
          await handleImport(rows);
        }}
      />
      <ImportGradeV2Modal
        open={importV2Open}
        onClose={() => {
          setImportV2Open(false);
        }}
        onUpload={handleUploadV2}
        onDownloadTemplate={async () =>
          await gradeApi.downloadGradeBlocksTemplate()
        }
      />

      <Dialog
        open={importResult !== null}
        onClose={() => {
          setImportResult(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.warning.main, 0.3),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('grade_importer_import_result_title')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.success.main, 0.08),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.success.main, 0.2),
              }}
            >
              <Typography variant="h5" fontWeight={700} color="success.main">
                {importResult?.created ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('grade_importer_result_success')}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: (th: Theme) => alpha(th.palette.error.main, 0.2),
              }}
            >
              <Typography variant="h5" fontWeight={700} color="error.main">
                {importResult?.failedGrades.length ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('grade_importer_result_failed')}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('grade_importer_result_body')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setImportResult(null);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('grade_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if (importResult) downloadFailedGrades(importResult.failedGrades);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('grade_importer_result_download')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmText('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.error.main, 0.25),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('grade_importer_delete_confirm_title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('grade_importer_delete_confirm_subtitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mb: 2,
              borderRadius: 2,
              bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.06),
              border: '1px solid',
              borderColor: (th: Theme) => alpha(th.palette.error.main, 0.2),
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              color="error.main"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {deleteTarget?.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('grade_importer_delete_confirm_instruction')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={deleteTarget?.name ?? ''}
            value={deleteConfirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDeleteConfirmText(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (
                e.key === 'Enter' &&
                deleteConfirmText === deleteTarget?.name
              ) {
                void confirmDelete();
              }
            }}
            error={
              deleteConfirmText.length > 0 &&
              deleteConfirmText !== deleteTarget?.name
            }
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteConfirmText('');
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('grade_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deleteTarget?.name}
            onClick={() => {
              void confirmDelete();
            }}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('grade_importer_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
